<template>
  <div>
    <v-card class="card-shadow border-radius-xl">
      <v-card-title>
        <span class="font-weight-bold text-h5 text-typo">月薪資結算</span>
      </v-card-title>
      <v-card-subtitle>
        <v-row>
          <v-col cols="12" md="2">
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              hide-details
              dense
              outlined
              class="
                mt-0
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              label="年份"
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="1">
            <v-select
              v-model="selectedMonth"
              :items="monthOptions"
              hide-details
              dense
              outlined
              class="
                mt-0
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              label="月份"
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="exportType"
              :items="filteredExportOptions"
              item-text="text"
              item-value="value"
              hide-details
              dense
              outlined
              class="
                mt-0
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              label="匯出"
            ></v-select>
          </v-col>
          <v-col
            cols="12"
            md="3"
            :class="[
              'd-flex',
              'justify-end',
              'align-end',
              { 'ml-auto': !showImport },
            ]"
          >
            <v-row class="pa-3 justify-end">
              <v-btn
                @click="exportMonthlySummary()"
                elevation="0"
                :ripple="false"
                height="43"
                :loading="exporting"
                :disabled="exportDisabled"
                class="
                  font-weight-bold
                  text-capitalize
                  btn-ls btn-primary
                  bg-gradient-primary
                  py-3
                  px-6
                  ms-3
                "
              >
                <v-icon size="18" class="me-1">mdi-download</v-icon>
                匯出Excel
              </v-btn>
              <v-btn
                @click="confirmResetAll"
                elevation="0"
                :ripple="false"
                height="43"
                :loading="exporting"
                :disabled="exportDisabled"
                class="
                  font-weight-bold
                  text-capitalize
                  btn-ls btn-default
                  bg-gradient-default
                  py-3
                  px-6
                  ms-3
                "
              >
                <v-icon size="18" class="me-1">mdi-download</v-icon>
                全部重設
              </v-btn>
            </v-row>
          </v-col>
          <v-col cols="12" md="4" v-if="showImport" class="d-flex justify-end">
            <v-row class="pa-3 justify-end">
              <v-file-input
                v-model="importFile"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hide-details
                dense
                outlined
                class="
                  mt-0
                  input-style
                  font-size-input
                  text-light-input
                  placeholder-light
                  input-icon
                "
                label="匯入檔案"
                prepend-icon="mdi-file-excel"
                :disabled="importing"
              />

              <v-btn
                @click="importMonthlyExcel"
                elevation="0"
                :ripple="false"
                height="43"
                :loading="importing"
                :disabled="importDisabled"
                class="
                  font-weight-bold
                  text-capitalize
                  btn-ls btn-default
                  bg-gradient-default
                  py-3
                  px-6
                  ms-3
                "
              >
                <v-icon size="18" class="me-1">mdi-upload</v-icon>
                匯入 Excel
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-card-subtitle>
      <v-card-text class="px-0 py-0">
        <v-data-table
          :headers="headers"
          :items="salaries"
          :loading="loading"
          :server-items-length="totalItems"
          class="table"
          hide-default-footer
          @page-count="pageCount = $event"
          disable-pagination
          :items-per-page="itemsPerPage"
          mobile-breakpoint="0"
          :item-class="rowClasses"
        >
          <template v-slot:header.salary_avg="{ header }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ header.text }}
                </span>
              </template>
              <span>前3個月薪資平均值的投保級距</span>
            </v-tooltip>
          </template>

          <template v-slot:header.bracket_max="{ header }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" class="bracket-column-label">
                  {{ header.text }}
                </span>
              </template>
              <span>雇主自主設定機制（可手動調整投保金額）</span>
            </v-tooltip>
          </template>

          <template v-slot:item.user.name="{ item }">
            <div class="d-flex align-center">
              <span
                class="text-sm font-weight-normal text-body text-primary cursor-pointer"
                @click.stop="goToUserProfile(item.user ? item.user.id : null)"
              >
                {{ item.user.name }}
                <v-tooltip bottom v-if="getEmployeeInfoTooltip(item)">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      :class="[
                        'text-xs',
                        'font-weight-bold',
                        'ms-1',
                        isResignedThisMonth(item) ? 'text-error' : 'text-info',
                      ]"
                    >
                      {{ isResignedThisMonth(item) ? "(離職)" : "(註)" }}
                    </span>
                  </template>
                  <span>{{ getEmployeeInfoTooltip(item) }}</span>
                </v-tooltip>
                <span
                  v-if="!isResignedThisMonth(item) && isNewEmployee(item)"
                  class="text-xs text-danger font-weight-bold"
                  >(新)</span
                >
              </span>
            </div>
          </template>

          <template v-slot:item.job_title="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.user ? item.user.job_title : "-" }}
              </span>
            </div>
          </template>

          <template v-slot:item.department="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.user ? item.user.department : "-" }}
              </span>
            </div>
          </template>

          <template v-slot:item.basic_salary="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.basic_salary"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div class="d-flex align-center bracket-column-cell" v-else>
              <span class="text-sm font-weight-normal text-body">
                {{ formatCurrency(item.basic_salary) }}
              </span>
              <v-tooltip bottom v-if="item.basic_salary === 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    small
                    class="text-error ms-1 bracket-mismatch-icon"
                    color="red"
                  >
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span
                  >請記得設定 員工薪資 僱用類別 合約薪水類別
                  並且重設該名員工</span
                >
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.supervisor_allowance="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.supervisor_allowance"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ formatCurrency(item.supervisor_allowance) }}
              </span>
            </div>
          </template>

          <template v-slot:item.position_allowance="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.position_allowance"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ formatCurrency(item.position_allowance) }}
              </span>
            </div>
          </template>

          <template v-slot:item.food_allowance="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.food_allowance"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ formatCurrency(item.food_allowance) }}
              </span>
            </div>
          </template>

          <template v-slot:item.bonus="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.bonus"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ formatCurrency(item.bonus) }}
              </span>
            </div>
          </template>

          <template v-slot:item.attendance_bonus="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.attendance_bonus"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ formatCurrency(item.attendance_bonus) }}
              </span>
            </div>
          </template>
          <!-- note user item.user.employeeProfile.note -->
          <template v-slot:item.note="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.user && item.user.employeeProfile ? item.user.employeeProfile.note : "-" }}
              </span>
            </div>
          </template>

          <template v-slot:item.non_wage_salary="{ item }">
            <div v-if="editingId === item.id">
              <v-btn
                small
                elevation="0"
                :ripple="false"
                height="30"
                class="
                  font-weight-bold
                  text-capitalize
                  btn-primary
                  bg-gradient-default
                  px-3
                "
                @click="openNonWageSalaryDialog"
                >編輯</v-btn
              >
              <div
                class="text-caption mt-1 text-truncate"
                style="max-width: 140px"
                :title="formatNonWageSalary(editedItem.non_wage_salary)"
              >
                {{ formatNonWageSalary(editedItem.non_wage_salary) }}
              </div>
            </div>
            <div v-else class="d-flex align-center">
              <span
                class="text-sm font-weight-normal text-body text-truncate"
                style="max-width: 140px"
                :title="formatNonWageSalary(item.non_wage_salary)"
              >
                {{ formatNonWageSalary(item.non_wage_salary) }}
              </span>
            </div>
          </template>

          <template v-slot:item.expected_sign_date="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{
                  item.user && item.user.employeeProfile
                    ? formatROCDate(
                        item.user.employeeProfile.expected_sign_date,
                      )
                    : "-"
                }}
              </span>
            </div>
          </template>

          <template v-slot:item.gross_pay="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ formatCurrency(item.gross_pay) }}
              </span>
            </div>
          </template>

          <template v-slot:item.bracket_max="{ item }">
            <div v-if="editingId === item.id">
              <v-autocomplete
                v-model.number="editedItem.bracket_max"
                :items="insuranceBracketOptions"
                item-text="text"
                item-value="value"
                dense
                hide-details
                class="edit-input"
                style="width: 120px; min-width: 120px"
                :filter="(item, queryText) => item.text.includes(queryText)"
                :menu-props="{ contentClass: 'bracket-autocomplete-menu' }"
                @update:menu="
                  (val) => onBracketMenuUpdate(val, editedItem.bracket_max)
                "
              >
                <template v-slot:item="{ item: option, on, attrs }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    :class="[
                      'bracket-option-item',
                      {
                        'bracket-option-selected': isBracketOptionSelected(
                          option.value,
                          editedItem.bracket_max,
                        ),
                      },
                    ]"
                    :data-bracket-option-selected="
                      isBracketOptionSelected(
                        option.value,
                        editedItem.bracket_max,
                      )
                        ? 'true'
                        : 'false'
                    "
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ option.text }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
            <v-edit-dialog
              :ref="'bracketEditDialog-' + item.id"
              v-else
              :return-value.sync="bracketEditValue"
              persistent
              @open="onBracketDialogOpen(item)"
              @save="onBracketDialogSave(item)"
              @cancel="onBracketDialogCancel"
            >
              <div class="d-flex align-center ">
                <span
                  class="text-sm font-weight-normal text-body bracket-column-cell"
                  style="width: 70px; min-width: 50px; display: inline-block"
                >
                  {{ formatCurrency(item.bracket_max) }}
                </span>
                <v-tooltip bottom v-if="hasBracketMismatch(item)">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      small
                      class="text-error ms-1 bracket-mismatch-icon"
                      color="red"
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>投保金額 低於預估投保金額</span>
                </v-tooltip>
              </div>
              <template v-slot:input>
                <v-autocomplete
                  v-model.number="bracketEditValue"
                  :items="insuranceBracketOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details
                  class="edit-input"
                  :filter="(item, queryText) => item.text.includes(queryText)"
                  :menu-props="{ contentClass: 'bracket-autocomplete-menu' }"
                  @update:menu="
                    (val) => onBracketMenuUpdate(val, bracketEditValue)
                  "
                >
                  <template v-slot:item="{ item: option, on, attrs }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      :class="[
                        'bracket-option-item',
                        {
                          'bracket-option-selected': isBracketOptionSelected(
                            option.value,
                            bracketEditValue,
                          ),
                        },
                      ]"
                      :data-bracket-option-selected="
                        isBracketOptionSelected(option.value, bracketEditValue)
                          ? 'true'
                          : 'false'
                      "
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ option.text }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
                <v-btn
                  small
                  color="primary"
                  class="
                    my-2
                    font-weight-bold
                    text-capitalize
                    btn-primary
                    bg-gradient-default
                    px-3
                  "
                  @click.stop="onBracketDialogConfirm(item)"
                >
                  確認
                </v-btn>
                <v-btn
                  small
                  class="my-2 ms-2 font-weight-bold text-capitalize px-3"
                  @click.stop="onBracketDialogCancelClick(item)"
                >
                  取消
                </v-btn>
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:item.salary_avg="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-bold text-body">
                <!-- {{ formatCurrency(item.salary_avg) }} -->
                <span
                  v-if="
                    getBracketMaxForSalary(
                      item.salary_avg,
                      item.bracket_max,
                    ) !== null
                  "
                  class="text-sm font-weight-normal text-secondary ms-1"
                >
                  <!-- / -->
                  {{
                    formatCurrency(
                      getBracketMaxForSalary(item.salary_avg, item.bracket_max),
                    )
                  }}
                </span>
              </span>
            </div>
          </template>

          <template v-slot:item.attendance_editor="{ item }">
            <div v-if="editingId === item.id">
              <!-- <v-btn
                small
                elevation="0"
                :ripple="false"
                height="30"
                class="font-weight-bold text-capitalize btn-primary bg-gradient-default px-3"
                @click="openAttendanceDialog"
              >
                編輯
              </v-btn> -->
              <div class="d-flex align-center mt-2">
                <v-checkbox
                  v-model="editedItem.ignore_late_early"
                  label="不計入遲到早退"
                  dense
                  hide-details
                  class="ma-0"
                  @change="onInlineIgnoreLateToggle"
                ></v-checkbox>
              </div>
            </div>
            <div v-else class="d-flex align-center">
              <span
                class="text-sm font-weight-normal text-body text-truncate"
                style="max-width: 160px"
              >
                {{ formatIgnoreLateEarly(item) }}
              </span>
            </div>
          </template>

          <template v-slot:item.personal_leave="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.personal_leave"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.personal_leave || 0 }}
              </span>
            </div>
          </template>

          <template v-slot:item.sick_leave="{ item }">
            <div v-if="editingId === item.id">
              <v-text-field
                v-model.number="editedItem.sick_leave"
                type="number"
                min="0"
                dense
                hide-details
                class="edit-input"
              ></v-text-field>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.sick_leave || 0 }}
              </span>
            </div>
          </template>

          <template v-slot:item.merged_insurance="{ item }">
            <div v-if="editingId === item.id">
              <v-btn
                small
                elevation="0"
                :ripple="false"
                height="30"
                class="
                  font-weight-bold
                  text-capitalize
                  btn-primary
                  bg-gradient-default
                  px-3
                "
                @click="openInsuranceDialog"
                >編輯</v-btn
              >
              <div class="text-caption mt-1">
                勞:{{ formatCurrency(editedItem.employee_labor_insurance) }} /
                健:{{ formatCurrency(editedItem.employee_health_insurance) }}
              </div>
            </div>
            <div v-else class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                勞:{{ formatCurrency(item.employee_labor_insurance) }} / 健:{{
                  formatCurrency(item.employee_health_insurance)
                }}
              </span>
            </div>
          </template>

          <template v-slot:item.company_extra_salary="{ item }">
            <div v-if="editingId === item.id">
              <v-btn
                small
                elevation="0"
                :ripple="false"
                height="30"
                class="
                  font-weight-bold
                  text-capitalize
                  btn-default
                  bg-gradient-default
                  px-3
                "
                @click="openCompanyExtraSalaryDialog"
                >編輯</v-btn
              >
              <div
                class="text-caption mt-1 text-truncate"
                style="max-width: 140px"
                :title="
                  formatCompanyExtraSalaryString(
                    editedItem.company_extra_salary,
                  )
                "
              >
                {{
                  formatCompanyExtraSalaryString(
                    editedItem.company_extra_salary,
                  )
                }}
              </div>
            </div>
            <div v-else class="d-flex align-center">
              <span
                class="text-sm font-weight-normal text-body text-truncate"
                style="max-width: 140px"
                :title="
                  formatCompanyExtraSalaryString(item.company_extra_salary)
                "
              >
                {{ formatCompanyExtraSalaryString(item.company_extra_salary) }}
              </span>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div v-if="editingId === item.id" class="d-flex align-center">
              <v-btn
                @click="saveEdit(item)"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls me-1 rounded-sm"
                color="success"
                :loading="saving"
              >
                <v-icon size="18">mdi-check</v-icon>
              </v-btn>
              <v-btn
                @click="cancelEdit"
                icon
                elevation="0"
                :ripple="false"
                height="28"
                min-width="36"
                width="36"
                class="btn-ls rounded-sm"
                color="error"
                :disabled="saving"
              >
                <v-icon size="18">mdi-close</v-icon>
              </v-btn>
            </div>
            <div v-else class="d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    @click="editItem(item)"
                    v-bind="attrs"
                    v-on="on"
                    icon
                    elevation="0"
                    :ripple="false"
                    height="28"
                    min-width="36"
                    width="36"
                    class="btn-ls me-1 rounded-sm"
                    color="primary"
                  >
                    <v-icon size="18">mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>編輯</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    @click="exportMonthlySummary(item.user_id, item.name)"
                    v-bind="attrs"
                    v-on="on"
                    icon
                    elevation="0"
                    :ripple="false"
                    height="28"
                    min-width="36"
                    width="36"
                    class="btn-ls rounded-sm"
                    color="success"
                  >
                    <v-icon size="18">mdi-file-excel</v-icon>
                  </v-btn>
                </template>
                <span>匯出 Excel</span>
              </v-tooltip>
              <!-- reset -->
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    @click="resetItem(item)"
                    v-bind="attrs"
                    v-on="on"
                    icon
                    elevation="0"
                    :ripple="false"
                    height="28"
                    min-width="36"
                    width="36"
                    class="btn-ls rounded-sm"
                    color="error"
                  >
                    <v-icon size="18">mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>重設</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>

        <div id="sentinel-salary-preview" class="d-flex justify-center py-4">
          <v-progress-circular
            v-if="loadingMore"
            indeterminate
            color="#cb0c9f"
            size="24"
          ></v-progress-circular>
          <v-btn
            v-else-if="fetchError"
            color="error"
            small
            outlined
            @click="
              salaries.length === 0 ? fetchSalaries() : fetchSalaries(0, true)
            "
          >
            <v-icon left>mdi-refresh</v-icon>
            重試
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Progress Dialog -->
    <v-dialog v-model="showProgressDialog" persistent max-width="400">
      <v-card>
        <v-card-text class="text-center py-5">
          <v-progress-circular
            indeterminate
            color="primary"
            size="50"
          ></v-progress-circular>
          <div class="mt-3">{{ progressMessage }}</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Company Extra Salary Dialog -->
    <v-dialog v-model="companyExtraSalaryDialog" max-width="720">
      <v-card>
        <v-card-title class="pb-0 d-flex align-center">
          <span class="text-h6">自訂加給設定</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeCompanyExtraSalaryDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" class="d-flex justify-end pb-0">
              <v-btn
                color="blue"
                dark
                class="white--text"
                @click="addCompanyExtraSalaryItem"
              >
                新增
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">項目名稱</th>
                    <th class="text-left">金額</th>
                    <th class="text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in companyExtraSalaryItems"
                    :key="`company-extra-${idx}`"
                  >
                    <td style="width: 55%">
                      <v-text-field
                        v-model="row.key"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </td>
                    <td style="width: 35%">
                      <v-text-field
                        v-model.number="row.value"
                        type="number"
                        min="0"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </td>
                    <td style="width: 10%">
                      <v-btn icon @click="removeCompanyExtraSalaryItem(idx)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCompanyExtraSalaryDialog">取消</v-btn>
          <v-btn
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            @click="confirmCompanyExtraSalaryDialog"
            >確認</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Non Wage Salary Dialog -->
    <v-dialog v-model="nonWageSalaryDialog" max-width="720">
      <v-card>
        <v-card-title class="pb-0 d-flex align-center">
          <span class="text-h6">非薪資項目設定</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeNonWageSalaryDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" class="d-flex justify-end pb-0">
              <v-btn
                color="blue"
                dark
                class="white--text"
                @click="addNonWageSalaryItem"
              >
                新增
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">項目名稱</th>
                    <th class="text-left">金額</th>
                    <th class="text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in nonWageSalaryItems"
                    :key="`non-wage-${idx}`"
                  >
                    <td style="width: 55%">
                      <v-text-field
                        v-model="row.key"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </td>
                    <td style="width: 35%">
                      <v-text-field
                        v-model.number="row.value"
                        type="number"
                        min="0"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </td>
                    <td style="width: 10%">
                      <v-btn icon @click="removeNonWageSalaryItem(idx)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeNonWageSalaryDialog">取消</v-btn>
          <v-btn
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            @click="confirmNonWageSalaryDialog"
            >確認</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-dialog
      :visible.sync="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :max-width="confirmDialog.maxWidth"
      :message-max-height="confirmDialog.messageMaxHeight"
      :message-align="confirmDialog.messageAlign"
      :yes-button-text="confirmDialog.confirmText"
      :no-button-text="confirmDialog.noButtonText"
      :yes-callback="confirmDialog.onConfirm"
    />

    <!-- Attendance Dialog -->
    <v-dialog v-model="attendanceDialog" max-width="600">
      <v-card>
        <v-card-title>
          出勤異常編輯
          <v-spacer></v-spacer>
          <v-btn icon @click="attendanceDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model.number="tempAttendance.late_count"
                label="遲到次数"
                type="number"
                min="0"
                dense
                disabled
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="tempAttendance.late"
                label="遲到時間(分)"
                type="number"
                min="0"
                dense
                disabled
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="tempAttendance.early_count"
                label="早退次数"
                type="number"
                min="0"
                dense
                disabled
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="tempAttendance.early"
                label="早退時間(分)"
                type="number"
                min="0"
                dense
                disabled
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="tempAttendance.absence_count"
                label="曠職次数"
                type="number"
                min="0"
                dense
                disabled
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="tempAttendance.absence_minutes"
                label="曠職時間(分)"
                type="number"
                min="0"
                dense
                disabled
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-checkbox
                v-model="tempAttendance.ignore_late_early"
                label="不計入遲到早退"
                dense
                hide-details
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="attendanceDialog = false">取消</v-btn>
          <v-btn
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            @click="confirmAttendanceDialog"
            >確認</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Insurance Dialog -->
    <v-dialog v-model="insuranceDialog" max-width="800">
      <v-card>
        <v-card-title>
          勞健保編輯
          <v-spacer></v-spacer>
          <v-btn icon @click="insuranceDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" dense outlined class="mb-4">
            提醒：當該月份尚未結束時，系統會忽略自行輸入的勞健保資料，並以重新計算的金額為準。
          </v-alert>
          <v-container>
            <v-row>
              <!-- Employer Side -->
              <v-col cols="12" md="6">
                <h3 class="text-subtitle-1 font-weight-bold mb-3">公司負擔</h3>
                <v-text-field
                  v-model.number="tempInsurance.employer_labor_insurance"
                  label="勞保"
                  type="number"
                  min="0"
                  dense
                  outlined
                ></v-text-field>
                <v-text-field
                  v-model.number="tempInsurance.employer_health_insurance"
                  label="健保"
                  type="number"
                  min="0"
                  dense
                  outlined
                ></v-text-field>
                <v-text-field
                  v-model.number="tempInsurance.employer_labor_pension"
                  label="勞退 (6%)"
                  type="number"
                  min="0"
                  dense
                  outlined
                ></v-text-field>
                <v-text-field
                  v-model.number="
                    tempInsurance.employer_occupational_accident_insurance
                  "
                  label="職災保險"
                  type="number"
                  min="0"
                  dense
                  outlined
                ></v-text-field>
                <v-text-field
                  v-model.number="tempInsurance.employer_employment_insurance"
                  label="就業保險"
                  type="number"
                  min="0"
                  dense
                  outlined
                ></v-text-field>
              </v-col>

              <!-- Employee Side -->
              <v-col cols="12" md="6">
                <h3 class="text-subtitle-1 font-weight-bold mb-3">員工負擔</h3>
                <v-text-field
                  v-model.number="tempInsurance.employee_labor_insurance"
                  label="勞保"
                  type="number"
                  min="0"
                  dense
                  outlined
                ></v-text-field>
                <v-text-field
                  v-model.number="tempInsurance.employee_health_insurance"
                  label="健保"
                  type="number"
                  min="0"
                  dense
                  outlined
                ></v-text-field>
                <!-- <v-text-field
                  v-model.number="tempInsurance.employee_welfare_fund"
                  label="福利金"
                  type="number"
                  min="0"
                  dense
                  outlined
                ></v-text-field> -->
                <v-checkbox
                  v-model="tempInsurance.employee_voluntary_pension"
                  label="勞退自提"
                  dense
                  hide-details
                  class="mb-2"
                ></v-checkbox>
                <v-select
                  v-if="tempInsurance.employee_voluntary_pension"
                  v-model.number="tempInsurance.employee_voluntary_pension_rate"
                  label="勞退自提比例(%)"
                  :items="[1, 2, 3, 4, 5, 6]"
                  dense
                  outlined
                ></v-select>

                <v-divider class="my-4"></v-divider>

                <div
                  class="d-flex justify-space-between align-center mt-4 mb-2"
                >
                  <h3 class="text-subtitle-1 font-weight-bold">
                    額外保險
                  </h3>
                  <v-btn
                    x-small
                    color="blue"
                    dark
                    class="white--text"
                    @click="addExtraInsuranceItem"
                  >
                    新增
                  </v-btn>
                </div>
                <div
                  v-for="(item, idx) in tempInsurance.extra_insurance_items"
                  :key="`extra-insurance-${idx}`"
                  class="d-flex align-center mb-2"
                >
                  <v-text-field
                    v-model="item.key"
                    dense
                    hide-details
                    class="mr-2"
                    style="width: 60%"
                  ></v-text-field>
                  <v-text-field
                    v-model.number="item.value"
                    type="number"
                    min="0"
                    dense
                    hide-details
                    style="width: 30%"
                  ></v-text-field>
                  <v-btn icon small @click="removeExtraInsuranceItem(idx)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="insuranceDialog = false">取消</v-btn>
          <v-btn
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            @click="confirmInsuranceDialog"
            >確認</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snackbar.show = false">關閉</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { formatNumber } from "@/utils/formatters";

import ConfirmDialog from "@/components/Common/ConfirmDialog.vue";

export default {
  name: "SalaryPreview",
  components: {
    ConfirmDialog,
  },
  props: {
    allowedExportTypes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    return {
      loading: false,
      showProgressDialog: false,
      progressMessage: "",
      salaries: [],
      page: 1,
      pageCount: 1,
      itemsPerPage: 15,
      totalItems: 0,
      loadingMore: false,
      fetchError: false,
      observer: null,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      yearOptions: Array.from({ length: 10 }, (_, i) => currentYear - 5 + i),
      monthOptions: Array.from({ length: 12 }, (_, i) => i + 1),
      exportType: "salary",
      exportOptions: [
        { text: "薪資明細表", value: "salary" },
        { text: "月薪資清冊", value: "list" },
      ],
      exporting: false,
      importFile: null,
      importing: false,
      nonWageSalaryDialog: false,
      nonWageSalaryItems: [],
      editingId: null,
      editedItem: {},
      saving: false,
      attendanceDialog: false,
      insuranceDialog: false,
      tempAttendance: {
        late: 0,
        late_count: 0,
        early: 0,
        early_count: 0,
        absence_count: 0,
        absence_minutes: 0,
        ignore_late_early: false,
      },
      tempInsurance: {
        employer_labor_insurance: 0,
        employer_health_insurance: 0,
        employer_labor_pension: 0,
        employer_occupational_accident_insurance: 0,
        employer_employment_insurance: 0,
        employee_labor_insurance: 0,
        employee_health_insurance: 0,
        employee_welfare_fund: 0,
        employee_voluntary_pension: false,
        employee_voluntary_pension_rate: 6,
        extra_insurance_items: [],
      },
      companyExtraSalaryDialog: false,
      companyExtraSalaryItems: [],
      confirmDialog: {
        show: false,
        title: "",
        message: "",
        color: "primary",
        maxWidth: 400,
        messageMaxHeight: "",
        messageAlign: "center",
        confirmText: "確認",
        onConfirm: () => {},
      },
      snackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },
      insuranceBrackets: [],
      bracketEditValue: null,
      headers: [
        {
          text: "姓名",
          sortable: false,
          value: "user.name",
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "100px",
        },
        // {
        //   text: "職位",
        //   value: "job_title",
        //   sortable: false,
        //   class: "text-secondary font-weight-bolder opacity-7 black--text",
        //   width: "80px",
        // },
        // 任職日期
        {
          text: "任職日期",
          value: "expected_sign_date",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "80px",
        },
        {
          text: "底薪",
          value: "basic_salary",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        // 主管加給 supervisor_allowance
        {
          text: "主管",
          value: "supervisor_allowance",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        // 職務加給 position_allowance
        {
          text: "職務",
          value: "position_allowance",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "績效",
          value: "bonus",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "伙食",
          value: "food_allowance",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        //全勤獎金
        {
          text: "全勤",
          value: "attendance_bonus",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        // 自訂加給 company_extra_salary
        {
          text: "自訂",
          value: "company_extra_salary",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "60px",
        },
        //non_wage_salary 非工資
        {
          text: "非工資",
          value: "non_wage_salary",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "40px",
        },
        {
          text: "應發",
          value: "gross_pay",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },

        {
          text: "遲到早退",
          value: "attendance_editor",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "60px",
        },

        // salary_avg
        {
          text: "預估級距",
          value: "salary_avg",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "投保金額",
          value: "bracket_max",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "勞健保",
          value: "merged_insurance",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "140px",
        },

        //實發
        {
          text: "實發",
          value: "net_pay",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        // note
        // {
        //   text: "備註",
        //   value: "note",
        //   sortable: false,
        //   class: "text-secondary font-weight-bolder opacity-7 black--text",
        // },
        {
          text: "操作",
          value: "actions",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "120px",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("user", ["userToken", "rootRole"]),
    ...mapGetters("company", ["companies", "selectedCompany"]),
    filteredExportOptions() {
      if (!this.allowedExportTypes || this.allowedExportTypes.length === 0) {
        return this.exportOptions;
      }
      return this.exportOptions.filter((option) =>
        this.allowedExportTypes.includes(option.value),
      );
    },
    showImport() {
      if (!this.rootRole) {
        return false;
      }
      return (
        !this.allowedExportTypes ||
        this.allowedExportTypes.length === 0 ||
        this.allowedExportTypes.includes("salary")
      );
    },
    company_id() {
      return this.selectedCompany ? this.selectedCompany.id : null;
    },
    hasMore() {
      return this.page < this.pageCount;
    },
    exportDisabled() {
      return (
        !this.company_id ||
        !this.selectedYear ||
        !this.selectedMonth ||
        this.exporting
      );
    },
    importDisabled() {
      return (
        !this.company_id ||
        !this.selectedYear ||
        !this.selectedMonth ||
        !this.importFile ||
        this.importing
      );
    },
    insuranceBracketOptions() {
      if (!Array.isArray(this.insuranceBrackets)) return [];
      return this.insuranceBrackets
        .filter((b) => b && b.bracket_max != null)
        .map((b) => ({
          text: `${b.bracket_max}`,
          value: Number(b.bracket_max),
        }));
    },
  },
  watch: {
    company_id: {
      handler(newVal) {
        if (newVal) {
          this.fetchSalaries();
        }
      },
      immediate: true,
    },
    allowedExportTypes: {
      handler() {
        this.validateExportType();
      },
      immediate: true,
    },
  },

  async mounted() {
    try {
      this.$root.$on("company-changed", this.handleCompanyChanged);
      this.$nextTick(() => {
        this.initIntersectionObserver();
      });
    } catch (error) {
      console.error("Error in mounted:", error);
      this.showSnackbar("Failed to initialize component", "error");
    }
  },
  beforeDestroy() {
    this.$root.$off("company-changed", this.handleCompanyChanged);
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    formatROCDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const year = date.getFullYear() - 1911;
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}/${month}/${day}`;
    },
    isNewEmployee(item) {
      if (
        item.user &&
        item.user.employeeProfile &&
        item.user.employeeProfile.expected_sign_date
      ) {
        const signDate = new Date(item.user.employeeProfile.expected_sign_date);
        const selectedYear = parseInt(this.selectedYear);
        const selectedMonth = parseInt(this.selectedMonth);
        return (
          signDate.getFullYear() === selectedYear &&
          signDate.getMonth() + 1 === selectedMonth
        );
      }
      return false;
    },
    getEmployeeNote(item) {
      return (item?.user?.employeeProfile?.note || "").toString().trim();
    },
    getEmployeeInfoTooltip(item) {
      const note = this.getEmployeeNote(item);
      const resignedThisMonth = this.isResignedThisMonth(item);
      const resignDate = this.formatROCDate(
        item?.user?.employeeProfile?.expected_resign_date,
      );

      if (note && resignedThisMonth) {
        return `備註：${note}\n離職日期：${resignDate}`;
      }
      if (note) {
        return `備註：${note}`;
      }
      if (resignedThisMonth) {
        return `離職日期：${resignDate}`;
      }
      return "";
    },
    isResignedThisMonth(item) {
      if (
        item.user &&
        item.user.employeeProfile &&
        item.user.employeeProfile.expected_resign_date
      ) {
        const resignDate = new Date(
          item.user.employeeProfile.expected_resign_date,
        );
        const selectedYear = parseInt(this.selectedYear);
        const selectedMonth = parseInt(this.selectedMonth);
        return (
          resignDate.getFullYear() === selectedYear &&
          resignDate.getMonth() + 1 === selectedMonth
        );
      }
      return false;
    },
    rowClasses(item) {
      if (this.isNewEmployee(item)) {
        return "light-blue lighten-5";
      }
      return "";
    },
    initIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver((entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          !this.loading &&
          !this.loadingMore &&
          !this.fetchError &&
          this.hasMore
        ) {
          console.log("fetchSalaries(0, true)");
          this.fetchSalaries(0, true);
        }
      }, options);

      const target = document.querySelector("#sentinel-salary-preview");
      if (target) {
        this.observer.observe(target);
      }
    },
    showSnackbar(text, type = "success") {
      this.snackbar.text = text;
      this.snackbar.color = type === "error" ? "error" : "success";
      this.snackbar.show = true;
    },
    onBracketDialogOpen(item) {
      this.bracketEditValue = Number(item.bracket_max) || null;
    },
    async onBracketDialogSave(item) {
      try {
        const value = Number(this.bracketEditValue);
        console.log("onBracketDialogSave", value);
        if (!Number.isFinite(value)) {
          this.showSnackbar("請選擇有效的投保金額", "error");
          return;
        }
        // Reuse saveEdit to keep logic consistent
        this.editedItem = { bracket_max: value };
        await this.saveEdit(item);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating bracket_max:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "更新失敗";
        this.showSnackbar(`更新投保金額失敗：${errorMsg}`, "error");
      } finally {
        this.bracketEditValue = null;
      }
    },
    onBracketDialogCancel() {
      this.bracketEditValue = null;
    },
    goToUserProfile(userId) {
      if (!userId) return;
      this.$router.push(`/pages/users/profile/${userId}`);
    },
    isBracketOptionSelected(optionValue, selectedValue) {
      return (
        selectedValue !== null &&
        selectedValue !== undefined &&
        Number(optionValue) === Number(selectedValue)
      );
    },
    onBracketDialogCancelClick(item) {
      const ref = this.$refs[`bracketEditDialog-${item.id}`];
      const dialog = Array.isArray(ref) ? ref[0] : ref;
      if (dialog) {
        dialog.isActive = false;
      }
      this.onBracketDialogCancel();
    },
    onBracketDialogConfirm(item) {
      const ref = this.$refs[`bracketEditDialog-${item.id}`];
      const dialog = Array.isArray(ref) ? ref[0] : ref;
      if (dialog) {
        dialog.isActive = false;
      }
      this.onBracketDialogSave(item);
    },
    onBracketMenuUpdate(val, selectedValue) {
      if (val) {
        this.$nextTick(() => {
          const tryScroll = (remaining) => {
            const menus = document.querySelectorAll(
              ".bracket-autocomplete-menu",
            );
            const menuEl = menus[menus.length - 1];
            if (!menuEl) {
              if (remaining > 0) setTimeout(() => tryScroll(remaining - 1), 60);
              return;
            }

            const activeItem =
              menuEl.querySelector("[data-bracket-option-selected='true']") ||
              menuEl.querySelector(".bracket-option-selected") ||
              menuEl.querySelector(".v-list-item[aria-selected='true']") ||
              menuEl.querySelector(".v-list-item--active") ||
              menuEl.querySelector(".v-list-item--highlighted");
            if (activeItem) {
              activeItem.scrollIntoView({ block: "center" });
              return;
            }

            if (selectedValue !== null && selectedValue !== undefined) {
              const fallback = Array.from(
                menuEl.querySelectorAll(".v-list-item"),
              ).find((el) => {
                const text = (el.textContent || "").trim();
                return (
                  text === String(selectedValue) ||
                  text.includes(String(selectedValue))
                );
              });
              if (fallback) {
                fallback.scrollIntoView({ block: "center" });
                return;
              }
            }

            if (remaining > 0) setTimeout(() => tryScroll(remaining - 1), 60);
          };

          tryScroll(12); // retry for ~720ms while menu renders/highlights
        });
      }
    },
    validateExportType() {
      const allowed =
        this.allowedExportTypes && this.allowedExportTypes.length > 0
          ? this.allowedExportTypes
          : this.exportOptions.map((o) => o.value);

      if (!allowed.includes(this.exportType)) {
        this.exportType = allowed[0];
      }
    },

    parseDownloadFilename(contentDisposition, fallbackFilename) {
      if (!contentDisposition) return fallbackFilename;

      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
        } catch (e) {
          return utf8Match[1].replace(/"/g, "");
        }
      }

      const match = contentDisposition.match(/filename=([^;]+)/i);
      if (match && match[1]) {
        return match[1].trim().replace(/^"|"$/g, "");
      }

      return fallbackFilename;
    },
    async exportMonthlySummary(user_id = null, name = null) {
      if (this.exportDisabled) return;

      const normalizedUserId =
        user_id && typeof user_id === "object" ? null : user_id;

      const escapeDialogText = (value) =>
        String(value ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");

      const salariesForExport = normalizedUserId
        ? this.salaries.filter((item) => {
            const itemUserId = Number(item?.user_id || item?.user?.id);
            return Number(normalizedUserId) === itemUserId;
          })
        : this.salaries;

      const getEmployeeName = (item) =>
        escapeDialogText(
          item?.user?.name || item?.name || `ID:${item?.user_id || item?.id || "-"}`,
        );

      const zeroBasicSalaryEmployees = salariesForExport
        .filter((item) => Number(item?.basic_salary) === 0)
        .map((item) => getEmployeeName(item));

      const noteEmployees = [];
      salariesForExport.forEach((item) => {
        const note = (item?.user?.employeeProfile?.note || "").toString().trim();
        if (!note) return;

        const normalizedNote = note.replace(/\s+/g, " ").trim();
        const preview =
          normalizedNote.length > 60
            ? `${normalizedNote.slice(0, 60)}...`
            : normalizedNote;
        noteEmployees.push(
          `${getEmployeeName(item)}：${escapeDialogText(preview)}`,
        );
      });

      const uniqueZeroBasicSalaryEmployees = [...new Set(zeroBasicSalaryEmployees)];
      console.log('uniqueZeroBasicSalaryEmployees', uniqueZeroBasicSalaryEmployees);

      if (
        uniqueZeroBasicSalaryEmployees.length > 0 ||
        noteEmployees.length > 0
      ) {
        const warningSections = [];
        if (uniqueZeroBasicSalaryEmployees.length > 0) {
          warningSections.push(
            `以下員工底薪為 0（共${uniqueZeroBasicSalaryEmployees.length}位）：\n${uniqueZeroBasicSalaryEmployees.join("、")}`,
          );
        }
        if (noteEmployees.length > 0) {
          warningSections.push(
            `以下員工有備註（共${noteEmployees.length}位）：\n${noteEmployees.join("\n")}`,
          );
        }

        this.confirmDialog = {
          show: true,
          title: "匯出提醒",
          message: `請檢查員工薪資、僱用類別、合約薪水類別設定。\n\n${warningSections.join("\n\n")}\n\n是否仍要匯出？`,
          color: "warning",
          maxWidth: 920,
          messageMaxHeight: "50vh",
          messageAlign: "left",
          confirmText: "匯出",
          noButtonText: "取消",
          onConfirm: async () => {
            this.confirmDialog.show = false;
            await this.executeExport(normalizedUserId, name);
          },
        };
        return;
      }
      await this.executeExport(normalizedUserId, name);
    },

    async executeExport(user_id = null, name = null) {
      try {
        this.exporting = true;
        this.showProgressDialog = true;
        this.progressMessage = "正在匯出 Excel...";

        let queryParams = `year=${this.selectedYear}&month=${this.selectedMonth}&company_id=${this.company_id}&export=${this.exportType}`;
        if (user_id) {
          queryParams += `&user_id=${user_id}`;
        }

        const response = await this.$axios.get(
          `/api/summary/monthly-summary?${queryParams}`,
          { responseType: "blob" },
        );

        const prefixMap = {
          attendance: "出勤紀錄表",
          salary: "薪資明細表",
          list: "薪資清冊",
        };
        const prefix = prefixMap[this.exportType] || this.exportType;
        const companyName =
          this.selectedCompany?.company_name || `company-${this.company_id}`;
        const filenameParts = [
          prefix,
          this.selectedYear,
          this.selectedMonth,
          companyName,
        ];
        if (user_id && name) {
          filenameParts.push(name);
        }
        const defaultFilename = `${filenameParts.join("-")}.xlsx`;
        // const filename = this.parseDownloadFilename(
        //   response.headers?.["content-disposition"],
        //   defaultFilename
        // );
        const filename = defaultFilename;
        console.log("exportType", this.exportType);

        const blob = new Blob([response.data], {
          type:
            response.headers?.["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        this.showSnackbar("匯出成功", "success");
      } catch (error) {
        console.error("Error exporting monthly summary:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "匯出失敗";
        this.showSnackbar(`匯出失敗：${errorMsg}`, "error");
      } finally {
        this.exporting = false;
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 200);
      }
    },
    async importMonthlyExcel() {
      if (this.importDisabled) return;

      try {
        this.importing = true;
        this.showProgressDialog = true;
        this.progressMessage = "正在匯入 Excel...";

        const file = Array.isArray(this.importFile)
          ? this.importFile[0]
          : this.importFile;

        if (!file) {
          this.showSnackbar("請先選擇要匯入的 Excel 檔案", "error");
          return;
        }

        const formData = new FormData();
        formData.append("file", file, file.name);
        formData.append("year", String(this.selectedYear));
        formData.append("month", String(this.selectedMonth));
        formData.append("company_id", String(this.company_id));

        await this.$axios.post(`/api/summary/parse-monthly-excel`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.showSnackbar("匯入成功", "success");
        this.importFile = null;
        await this.fetchSalaries(1);
      } catch (error) {
        console.error("Error importing monthly excel:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "匯入失敗";
        this.showSnackbar(`匯入失敗：${errorMsg}`, "error");
      } finally {
        this.importing = false;
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 200);
      }
    },
    async fetchSalaries(reset = 0, append = false) {
      try {
        // if not this.company_id, return
        if (!this.company_id) {
          return;
        }
        if (append) {
          if (!this.hasMore) return;
          this.loadingMore = true;
          this.page += 1;
        } else {
          this.loading = true;
          this.showProgressDialog = true;
          this.progressMessage = "載入薪資資料...";
          this.page = 1;
          this.salaries = [];
        }
        this.fetchError = false;

        let queryParams = `page=${this.page}&pageSize=${this.itemsPerPage}`;

        if (this.selectedYear) {
          queryParams += `&year=${this.selectedYear}`;
        }

        if (this.selectedMonth) {
          queryParams += `&month=${this.selectedMonth}`;
        }

        if (this.company_id) {
          queryParams += `&company_id=${this.company_id}`;
        }

        let effectiveReset = reset;
        const selectedYear = Number(this.selectedYear);
        const selectedMonth = Number(this.selectedMonth);
        if (
          Number.isFinite(selectedYear) &&
          Number.isFinite(selectedMonth)
        ) {
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth() + 1;
          const isCurrentOrFuture =
            selectedYear > currentYear ||
            (selectedYear === currentYear && selectedMonth >= currentMonth);
          if (isCurrentOrFuture) {
            effectiveReset = 1;
          }
        }

        //add reset parameter to force reload
        queryParams += `&reset=${effectiveReset}`;
        // queryParams += `&reset=1`;

        const response = await this.$axios.get(
          `/api/salaries/preview?${queryParams}`,
        );

        const responseData = response.data.data || {};
        this.insuranceBrackets = responseData.insuranceBrackets || [];

        const newItems = responseData.salaries || [];
        const pagination = responseData.pagination || {
          totalPages: 1,
          total: newItems.length,
        };
        this.pageCount = pagination.totalPages;
        this.totalItems = pagination.total;

        if (append) {
          this.salaries = [...this.salaries, ...newItems];
        } else {
          this.salaries = newItems;
        }
      } catch (error) {
        if (append && this.page > 1) {
          this.page -= 1;
        }
        console.error("Error fetching salaries:", error);
        this.fetchError = true;
        this.progressMessage = "載入資料失敗...";
        this.showSnackbar(this.progressMessage, "error");
      } finally {
        this.loading = false;
        this.loadingMore = false;
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 200);
      }
    },
    formatNonWageSalary(data) {
      if (!data || typeof data !== "object" || Object.keys(data).length === 0)
        return "-";
      return Object.entries(data)
        .map(([k, v]) => `${k}:${Number(v).toLocaleString()}`)
        .join("，");
    },
    openNonWageSalaryDialog() {
      const data = this.editedItem.non_wage_salary;
      this.nonWageSalaryItems =
        data && typeof data === "object"
          ? Object.keys(data).map((k) => ({
              key: k,
              value: Number(data[k]) || 0,
            }))
          : [];
      this.nonWageSalaryDialog = true;
    },
    closeNonWageSalaryDialog() {
      this.nonWageSalaryDialog = false;
      this.nonWageSalaryItems = [];
    },
    addNonWageSalaryItem() {
      this.nonWageSalaryItems.push({ key: "", value: 0 });
    },
    removeNonWageSalaryItem(idx) {
      this.nonWageSalaryItems.splice(idx, 1);
    },
    confirmNonWageSalaryDialog() {
      const map = {};
      this.nonWageSalaryItems
        .filter(
          (it) =>
            (it?.key || "").toString().trim() !== "" ||
            (Number(it?.value) || 0) > 0,
        )
        .forEach((it) => {
          const k = (it?.key || "").toString().trim() || "未命名";
          const v = Number(it?.value) || 0;
          map[k] = v;
        });
      this.editedItem.non_wage_salary = map;
      this.closeNonWageSalaryDialog();
    },
    formatCompanyExtraSalaryString(data) {
      if (!data || typeof data !== "object" || Object.keys(data).length === 0)
        return "未設定";
      const entries = Object.entries(data);

      // If only one item, show the detail
      if (entries.length === 1) {
        const [key, value] = entries[0];
        const val = Number(value) || 0;
        return `${key}:${val.toLocaleString()}`;
      }

      // If more than one item, show summary: "共X筆 $總金額"
      const total = entries.reduce((sum, [, value]) => {
        return sum + (Number(value) || 0);
      }, 0);
      return `共${entries.length}筆 $${total.toLocaleString()}`;
    },
    openCompanyExtraSalaryDialog() {
      const data = this.editedItem.company_extra_salary;

      this.companyExtraSalaryItems =
        data && typeof data === "object"
          ? Object.keys(data).map((k) => ({
              key: k,
              value: Number(data[k]) || 0,
            }))
          : [];
      this.companyExtraSalaryDialog = true;
    },
    closeCompanyExtraSalaryDialog() {
      this.companyExtraSalaryDialog = false;
      this.companyExtraSalaryItems = [];
    },
    addCompanyExtraSalaryItem() {
      this.companyExtraSalaryItems.push({ key: "", value: 0 });
    },
    removeCompanyExtraSalaryItem(idx) {
      this.companyExtraSalaryItems.splice(idx, 1);
    },
    confirmCompanyExtraSalaryDialog() {
      const map = {};
      this.companyExtraSalaryItems
        .filter(
          (it) =>
            (it?.key || "").toString().trim() !== "" ||
            (Number(it?.value) || 0) > 0,
        )
        .forEach((it) => {
          const k = (it?.key || "").toString().trim() || "未命名";
          const v = Number(it?.value) || 0;
          map[k] = v;
        });
      this.editedItem.company_extra_salary = map;
      this.closeCompanyExtraSalaryDialog();
    },
    openAttendanceDialog() {
      this.tempAttendance = {
        late: this.editedItem.late || 0,
        late_count: this.editedItem.late_count || 0,
        early: this.editedItem.early || 0,
        early_count: this.editedItem.early_count || 0,
        absence_count: this.editedItem.absence_count || 0,
        absence_minutes: this.editedItem.absence_minutes || 0,
        ignore_late_early: !!this.editedItem.ignore_late_early,
      };
      this.attendanceDialog = true;
    },
    confirmAttendanceDialog() {
      const late = Number(this.tempAttendance.late) || 0;
      const late_count = Number(this.tempAttendance.late_count) || 0;
      const early = Number(this.tempAttendance.early) || 0;
      const early_count = Number(this.tempAttendance.early_count) || 0;
      const absence_count = Number(this.tempAttendance.absence_count) || 0;
      const absence_minutes = Number(this.tempAttendance.absence_minutes) || 0;
      const ignore_late_early = !!this.tempAttendance.ignore_late_early;

      // Validation: If time > 0, count must be > 0. If count > 0, time must be > 0.
      // Only validate if not ignored
      if (!ignore_late_early) {
        if ((late > 0 && late_count === 0) || (late_count > 0 && late === 0)) {
          this.showSnackbar("遲到時間與次數需同時輸入且大於 0", "error");
          return;
        }
        if (
          (early > 0 && early_count === 0) ||
          (early_count > 0 && early === 0)
        ) {
          this.showSnackbar("早退時間與次數需同時輸入且大於 0", "error");
          return;
        }
        if (
          (absence_minutes > 0 && absence_count === 0) ||
          (absence_count > 0 && absence_minutes === 0)
        ) {
          this.showSnackbar("曠職時間與次數需同時輸入且大於 0", "error");
          return;
        }
      }

      this.editedItem.late = late;
      this.editedItem.late_count = late_count;
      this.editedItem.early = early;
      this.editedItem.early_count = early_count;
      this.editedItem.absence_count = absence_count;
      this.editedItem.absence_minutes = absence_minutes;
      this.editedItem.ignore_late_early = ignore_late_early;
      this.attendanceDialog = false;
    },
    openInsuranceDialog() {
      this.tempInsurance = {
        employer_labor_insurance: this.editedItem.employer_labor_insurance || 0,
        employer_health_insurance:
          this.editedItem.employer_health_insurance || 0,
        employer_labor_pension: this.editedItem.employer_labor_pension || 0,
        employer_occupational_accident_insurance:
          this.editedItem.employer_occupational_accident_insurance || 0,
        employer_employment_insurance:
          this.editedItem.employer_employment_insurance || 0,

        employee_labor_insurance: this.editedItem.employee_labor_insurance || 0,
        employee_health_insurance:
          this.editedItem.employee_health_insurance || 0,
        employee_welfare_fund: this.editedItem.employee_welfare_fund || 0,
        employee_voluntary_pension:
          this.editedItem.employee_voluntary_pension || false,
        employee_voluntary_pension_rate:
          this.editedItem.employee_voluntary_pension_rate || 6,
        extra_insurance_items: [],
      };

      // Handle extra_insurance parsing
      let extraInsuranceData = {};
      if (this.editedItem.extra_insurance) {
        try {
          extraInsuranceData =
            typeof this.editedItem.extra_insurance === "string"
              ? JSON.parse(this.editedItem.extra_insurance)
              : JSON.parse(JSON.stringify(this.editedItem.extra_insurance));
        } catch (e) {
          console.error("Failed to parse extra_insurance:", e);
        }
      }

      const migratedExtraInsurance = {
        ...(extraInsuranceData && typeof extraInsuranceData === "object"
          ? extraInsuranceData
          : {}),
      };

      // Normalize old format if exists
      if (
        Array.isArray(migratedExtraInsurance.dependent_health_insurance_items)
      ) {
        migratedExtraInsurance.dependent_health_insurance_items.forEach(
          (it) => {
            const k = (it?.key || "").toString().trim() || "眷屬健保";
            const v = Number(it?.value) || 0;
            if (v > 0) migratedExtraInsurance[k] = v;
          },
        );
      }
      if (migratedExtraInsurance.dependent_health_insurance) {
        const v =
          Number(migratedExtraInsurance.dependent_health_insurance) || 0;
        if (v > 0) migratedExtraInsurance["眷屬健保"] = v;
      }
      // Remove known non-extra fields from this object to avoid duplication if they were stored there
      delete migratedExtraInsurance.employee_voluntary_pension;
      delete migratedExtraInsurance.dependent_health_insurance;
      delete migratedExtraInsurance.dependent_health_insurance_items;

      this.tempInsurance.extra_insurance_items = Object.keys(
        migratedExtraInsurance,
      ).map((k) => ({
        key: k,
        value: Number(migratedExtraInsurance[k]) || 0,
      }));

      this.insuranceDialog = true;
    },
    async confirmInsuranceDialog() {
      const map = {};
      this.tempInsurance.extra_insurance_items
        .filter(
          (it) =>
            (it?.key || "").toString().trim() !== "" ||
            (Number(it?.value) || 0) > 0,
        )
        .forEach((it) => {
          const k = (it?.key || "").toString().trim() || "眷屬健保";
          const v = Number(it?.value) || 0;
          map[k] = v;
        });

      const insuranceUpdateData = {
        employer_labor_insurance:
          Number(this.tempInsurance.employer_labor_insurance) || 0,
        employer_health_insurance:
          Number(this.tempInsurance.employer_health_insurance) || 0,
        employer_labor_pension:
          Number(this.tempInsurance.employer_labor_pension) || 0,
        employer_occupational_accident_insurance:
          Number(this.tempInsurance.employer_occupational_accident_insurance) ||
          0,
        employer_employment_insurance:
          Number(this.tempInsurance.employer_employment_insurance) || 0,
        employee_labor_insurance:
          Number(this.tempInsurance.employee_labor_insurance) || 0,
        employee_health_insurance:
          Number(this.tempInsurance.employee_health_insurance) || 0,
        employee_welfare_fund:
          Number(this.tempInsurance.employee_welfare_fund) || 0,
        employee_voluntary_pension: !!this.tempInsurance
          .employee_voluntary_pension,
        employee_voluntary_pension_rate:
          Number(this.tempInsurance.employee_voluntary_pension_rate) || 6,
        extra_insurance: map,
      };

      Object.assign(this.editedItem, insuranceUpdateData);
      this.insuranceDialog = false;

      const editingItem = this.salaries.find((s) => s.id === this.editingId);
      if (!editingItem) {
        this.showSnackbar("找不到可儲存的薪資資料", "error");
        return;
      }

      try {
        this.saving = true;
        this.showProgressDialog = true;
        this.progressMessage = "儲存勞健保資料...";

        await this.$axios.put(
          `/api/salaries/${editingItem.id}/insurance`,
          insuranceUpdateData,
        );

        await this.fetchSalaries(0);
        this.showSnackbar("勞健保資料已更新", "success");
        this.editingId = null;
        this.editedItem = {};
      } catch (error) {
        console.error("Error updating insurance:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "更新失敗";
        this.showSnackbar(`更新勞健保失敗：${errorMsg}`, "error");
      } finally {
        this.saving = false;
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 200);
      }
    },
    addExtraInsuranceItem() {
      this.tempInsurance.extra_insurance_items.push({
        key: "眷屬健保",
        value: 0,
      });
    },
    removeExtraInsuranceItem(idx) {
      this.tempInsurance.extra_insurance_items.splice(idx, 1);
    },
    onFilterChange() {
      this.page = 1;
      this.fetchSalaries(0, false);
      console.log("onFilterChange");
    },
    async handleCompanyChanged(company) {
      if (company && company.id !== this.company_id) {
        this.page = 1;
        await this.fetchSalaries(0, false);
        console.log("handleCompanyChanged");
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return formatNumber(value);
    },
    formatIgnoreLateEarly(source, useEdited = false) {
      if (!source) return "-";
      const value = useEdited
        ? !!source.ignore_late_early
        : !!(source.ignore_late_early ?? source.extra?.ignore_late_early);
      return value ? "不列入" : "列入";
    },
    onInlineIgnoreLateToggle() {
      if (!this.editedItem) return;
      this.editedItem.ignore_late_early = !!this.editedItem.ignore_late_early;
    },
    getBracketForSalary(salaryAvg) {
      if (
        !Array.isArray(this.insuranceBrackets) ||
        this.insuranceBrackets.length === 0
      ) {
        return null;
      }
      const target = Number(salaryAvg);
      if (!Number.isFinite(target)) return null;
      const bracket = this.insuranceBrackets.find((b) => {
        const min = Number(b.bracket_min);
        const max = Number(b.bracket_max);
        return (
          Number.isFinite(min) &&
          Number.isFinite(max) &&
          min <= target &&
          max >= target
        );
      });
      if (bracket) return bracket;
      const eligible = this.insuranceBrackets
        .filter((b) => Number(b.bracket_min) <= target)
        .sort((a, b) => Number(a.bracket_max) - Number(b.bracket_max));
      return eligible.length ? eligible[eligible.length - 1] : null;
    },
    lookupBracketMaxBySalary(salaryAvg) {
      const bracket = this.getBracketForSalary(salaryAvg);
      if (bracket && bracket.bracket_max != null) {
        return Number(bracket.bracket_max);
      }
      return null;
    },
    getBracketMaxForSalary(salaryAvg, fallbackValue = null) {
      const computed = this.lookupBracketMaxBySalary(salaryAvg);
      if (computed != null) {
        return computed;
      }
      if (fallbackValue != null) {
        return Number(fallbackValue);
      }
      return null;
    },
    hasBracketMismatch(item) {
      if (!item) return false;
      const apiValue = item.bracket_max;
      const computed = this.lookupBracketMaxBySalary(item.salary_avg);
      if (computed == null || apiValue == null) {
        return false;
      }
      return Number(computed) > Number(apiValue);
    },
    editItem(item) {
      this.editingId = item.id;
      this.editedItem = {
        basic_salary: item.basic_salary,
        supervisor_allowance: item.supervisor_allowance,
        position_allowance: item.position_allowance,
        food_allowance: item.food_allowance,
        bonus: item.bonus,
        attendance_bonus: item.attendance_bonus,
        other_earnings: item.other_earnings,
        salary_avg: item.salary_avg,
        bracket_max: item.bracket_max,
        work_0_2: item.work_0_2,
        work_2_4: item.work_2_4,
        rest_0_2: item.rest_0_2,
        rest_2_8: item.rest_2_8,
        rest_8_12: item.rest_8_12,
        nat_0_8: item.nat_0_8,
        nat_9_10: item.nat_9_10,
        nat_11_12: item.nat_11_12,
        personal_leave: item.personal_leave,
        sick_leave: item.sick_leave,
        extra_leave: item.extra_leave,
        late: item.late,
        late_count: item.late_count,
        early: item.early,
        early_count: item.early_count,
        absence_count: item.absence_count,
        absence_minutes: item.absence_minutes,
        ignore_late_early: !!item.extra?.ignore_late_early,
        other_deductions: item.other_deductions,
        employer_labor_insurance: item.employer_labor_insurance,
        employer_health_insurance: item.employer_health_insurance,
        employer_labor_pension: item.employer_labor_pension,
        employer_occupational_accident_insurance:
          item.employer_occupational_accident_insurance,
        employer_employment_insurance: item.employer_employment_insurance,
        employee_labor_insurance: item.employee_labor_insurance,
        employee_health_insurance: item.employee_health_insurance,
        employee_welfare_fund: item.employee_welfare_fund,
        employee_voluntary_pension: item.employee_voluntary_pension,
        employee_voluntary_pension_rate: item.employee_voluntary_pension_rate,
        company_extra_salary:
          item.company_extra_salary &&
          typeof item.company_extra_salary === "object"
            ? JSON.parse(JSON.stringify(item.company_extra_salary))
            : {},
        extra_insurance:
          item.extra_insurance && typeof item.extra_insurance === "object"
            ? JSON.parse(JSON.stringify(item.extra_insurance))
            : {},
        non_wage_salary:
          item.non_wage_salary && typeof item.non_wage_salary === "object"
            ? JSON.parse(JSON.stringify(item.non_wage_salary))
            : {},
      };
    },
    cancelEdit() {
      this.editingId = null;
      this.editedItem = {};
    },
    async saveEdit(item) {
      try {
        this.saving = true;
        this.showProgressDialog = true;
        this.progressMessage = "儲存薪資資料...";

        const updateData = {};
        const objectLikeKeys = [
          "company_extra_salary",
          "extra_insurance",
          "non_wage_salary",
        ];
        Object.keys(this.editedItem).forEach((key) => {
          const editedValue = this.editedItem[key];
          if (editedValue === null || editedValue === undefined) {
            return;
          }

          let originalValue;
          if (key === "ignore_late_early") {
            originalValue = !!(item.extra?.ignore_late_early ?? false);
          } else {
            originalValue = item[key];
          }

          if (objectLikeKeys.includes(key)) {
            originalValue =
              originalValue && typeof originalValue === "object"
                ? originalValue
                : {};
          }

          const bothNumbers =
            Number.isFinite(Number(editedValue)) &&
            Number.isFinite(Number(originalValue));

          const isSameValue =
            editedValue === originalValue ||
            (bothNumbers && Number(editedValue) === Number(originalValue)) ||
            (typeof editedValue === "object" &&
              typeof originalValue === "object" &&
              JSON.stringify(editedValue) === JSON.stringify(originalValue));

          if (!isSameValue) {
            updateData[key] = editedValue;
          }
        });

        if (Object.keys(updateData).length === 0) {
          this.showSnackbar("沒有欄位異動", "info");
          this.editingId = null;
          this.editedItem = {};
          return;
        }

        await this.$axios.put(`/api/salaries/${item.id}`, updateData);

        await this.fetchSalaries(0);

        this.showSnackbar("薪資資料已更新", "success");
        this.editingId = null;
        this.editedItem = {};
      } catch (error) {
        console.error("Error updating salary:", error);
        const errorMsg =
          error.response?.data?.message || error.message || "更新失敗";
        this.showSnackbar(`更新薪資失敗：${errorMsg}`, "error");
      } finally {
        this.saving = false;
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 200);
      }
    },
    async resetItem(item) {
      this.confirmDialog = {
        show: true,
        title: "重置確認",
        message: `確定要重置 ${
          item.user?.name || ""
        } 的薪資資料嗎？這將重新計算並覆蓋手動修改的內容。`,
        color: "error",
        confirmText: "重置",
        noButtonText: "取消",
        onConfirm: async () => {
          this.confirmDialog.show = false;
          try {
            this.showProgressDialog = true;
            this.progressMessage = "正在重置薪資資料...";

            const response = await this.$axios.post(
              `/api/salaries/${item.id}/reset`,
            );

            const updatedSalary = response.data.data.salary;
            const index = this.salaries.findIndex((s) => s.id === item.id);
            if (index !== -1) {
              this.$set(this.salaries, index, {
                ...this.salaries[index],
                ...updatedSalary,
              });
            }

            this.showSnackbar("薪資資料已重置", "success");
          } catch (error) {
            console.error("Error resetting salary:", error);
            const errorMsg =
              error.response?.data?.message || error.message || "重置失敗";
            this.showSnackbar(`重置失敗：${errorMsg}`, "error");
          } finally {
            setTimeout(() => {
              this.showProgressDialog = false;
            }, 200);
          }
        },
      };
    },
    confirmResetAll() {
      const year = this.selectedYear;
      const month = this.selectedMonth;
      const companyName = this.selectedCompany?.company_name || "";

      this.confirmDialog = {
        show: true,
        title: "全部重設確認",
        message: `確定要重置 ${companyName} ${year}年${month}月 的所有薪資資料嗎？這將重新計算本月所有員工資料並覆蓋任何手動修改。`,
        color: "error",
        confirmText: "全部重設",
        noButtonText: "取消",
        onConfirm: () => {
          this.confirmDialog.show = false;
          this.fetchSalaries(1);
        },
      };
    },
  },
};
</script>

<style scoped>
.table >>> thead th {
  white-space: nowrap;
  font-size: 1rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: #8392ab;
  opacity: 0.7;
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:last-child td {
  border-bottom: 0;
}

.table >>> tbody td {
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:hover {
  background: #f8f9fa;
}

.input-style {
  background-color: #f8f9fa;
  border-radius: 0.5rem;
}

.font-size-input {
  font-size: 0.875rem;
}

.text-light-input {
  color: #495057;
}

.placeholder-light::placeholder {
  color: #adb5bd;
}

.card-shadow {
  box-shadow: 0 20px 27px 0 rgba(0, 0, 0, 0.05);
}

.border-radius-xl {
  border-radius: 1rem;
}

.edit-input {
  max-width: 120px;
}

.edit-input >>> .v-input__control {
  min-height: 32px;
}

.edit-input >>> input {
  font-size: 0.875rem;
}

/* Hide number spinners for inline edit inputs */
.edit-input >>> input[type="number"]::-webkit-outer-spin-button,
.edit-input >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.edit-input >>> input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.bracket-column-label {
  border: 1px solid rgba(244, 67, 54, 0.5);
  padding: 2px 8px;
  border-radius: 6px;
  color: #e53935;
}

.bracket-column-cell {
  border: 1px solid rgba(244, 67, 54, 0.5);
  border-radius: 6px;
  padding: 4px 8px;
}

.bracket-mismatch-icon {
  animation: blink-warning 3s infinite;
}

.bracket-option-item.bracket-option-selected {
  background-color: #e8f5e9 !important;
  color: #1b5e20 !important;
}

.bracket-option-item.bracket-option-selected .v-list-item__title {
  color: #1b5e20 !important;
  font-weight: 700;
}

@keyframes blink-warning {
  0% {
    opacity: 0.2;
    transform: scale(0.95);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 0.2;
    transform: scale(0.95);
  }
}
</style>
